import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FiChevronLeft,
    FiChevronRight,
    FiEdit3,
    FiPlus,
    FiSearch,
    FiTrash2,
    FiMoreVertical,
    FiX,
    FiRefreshCw,
} from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';
import { getAuthContext } from "../../utils/jwt";

// Icon type casts
const FiPlusComponent = FiPlus as unknown as React.ComponentType<{ size?: number | string; className?: string }>;
const FiEdit3Component = FiEdit3 as unknown as React.ComponentType<{ size?: number | string; className?: string }>;
const FiTrash2Component = FiTrash2 as unknown as React.ComponentType<{ size?: number | string; className?: string }>;
const FiSearchComponent = FiSearch as unknown as React.ComponentType<{ size?: number | string; className?: string }>;
const FiMoreVerticalComponent = FiMoreVertical as unknown as React.ComponentType<{ size?: number | string; className?: string }>;
const FiChevronLeftComponent = FiChevronLeft as unknown as React.ComponentType<{ size?: number | string; className?: string }>;
const FiChevronRightComponent = FiChevronRight as unknown as React.ComponentType<{ size?: number | string; className?: string }>;
const FiXComponent = FiX as unknown as React.ComponentType<{ size?: number | string; className?: string }>;
const FiRefreshCwComponent = FiRefreshCw as unknown as React.ComponentType<{ size?: number | string; className?: string }>;

interface Segment {
    id: string;
    name: string;
    type?: string;
    description?: string | null;
    rulesJson?: any[];
    createdAt?: string;
    updatedAt?: string;
}

// Custom debounce hook
const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

// Simple pagination page-number builder (matches screenshot style)
const getPageNumbers = (current: number, total: number): (number | '...')[] => {
    const pages: (number | '...')[] = [];
    const delta = 1;

    if (total <= 1) return [1];

    pages.push(1);

    const rangeStart = Math.max(2, current - delta + 1);
    const rangeEnd = Math.min(total - 1, current + delta + 1);

    if (rangeStart > 2) pages.push('...');

    for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);

    if (rangeEnd < total - 1) pages.push('...');

    if (total > 1) pages.push(total);

    return pages;
};

const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '—';
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '—';
        return d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    } catch {
        return '—';
    }
};

const Segments: React.FC = () => {
    const navigate = useNavigate();

    const [segments, setSegments] = useState<Segment[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // selection
    const [selected, setSelected] = useState<string[]>([]);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);

    // delete modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [segmentToDelete, setSegmentToDelete] = useState<Segment | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // refresh state
    const [isRefreshing, setIsRefreshing] = useState(false);

    // pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(6);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);

    // search state
    const [isSearching, setIsSearching] = useState(false);
    const debouncedSearchTerm = useDebounce(searchTerm, 1000); // 1 second delay

    // ==================== FETCH SEGMENTS ====================
    const fetchSegments = async (page: number = 0, size: number = pageSize, search: string = '') => {
        const { token, tenantId, userId } = getAuthContext();
        if (!token) {
            setLoading(false);
            return;
        }

        const isSearchRequest = search.trim().length > 0;

        if (isSearchRequest) {
            setIsSearching(true);
        } else {
            setLoading(true);
        }

        try {
            // Build URL:
            // - Search mode: `?search=<encoded>`
            // - Normal mode: `?page=<n>&size=<n>`
            const url = isSearchRequest
                ? `${BASE_URL}/v1/audience-insights/segments?search=${encodeURIComponent(search.trim())}`
                : `${BASE_URL}/v1/audience-insights/segments?page=${page}&size=${size}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'X-Tenant-Id': tenantId,
                    'X-User-Id': userId || '',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const data = await response.json();

            // Handle both paginated response and plain array response
            const rawContent = Array.isArray(data) ? data : (data.content || []);
            const content: Segment[] = rawContent.map((seg: any) => ({
                id: seg.id,
                name: seg.name,
                type: seg.type,
                description: seg.description,
                rulesJson: seg.rulesJson || [],
                createdAt: seg.createdAt,
                updatedAt: seg.updatedAt || seg.lastModifiedAt,
            }));

            setSegments(content);

            if (isSearchRequest) {
                // Search response isn't paginated — just show all results
                setTotalPages(1);
                setTotalElements(content.length);
                setCurrentPage(1);
            } else {
                setTotalPages(data.totalPages || 1);
                setTotalElements(data.totalElements || content.length);
                setCurrentPage((data.pageable?.pageNumber ?? page) + 1);
            }
        } catch (error) {
            // console.error('❌ Failed to fetch segments:', error);
            toast.error(isSearchRequest ? 'Failed to search segments' : 'Failed to load segments');
        } finally {
            if (isSearchRequest) {
                setIsSearching(false);
            } else {
                setLoading(false);
            }
        }
    };

    // Initial fetch and refetch on pageSize change
    useEffect(() => {
        fetchSegments(0, pageSize, '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSize]);

    // Trigger fetch when debounced search term changes
    useEffect(() => {
        if (debouncedSearchTerm.trim()) {
            fetchSegments(0, pageSize, debouncedSearchTerm);
        } else {
            // Search cleared → revert to normal paginated view
            setCurrentPage(1);
            fetchSegments(0, pageSize, '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchTerm]);

    // Close dropdown menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setOpenMenuId(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // ==================== FILTERED SEGMENTS ====================
    // No client-side filtering needed — server handles it now
    const filteredSegments = useMemo(() => segments, [segments]);

    // Determines whether we are currently in search mode
    const isSearchMode = debouncedSearchTerm.trim().length > 0;

    // ==================== REFRESH HANDLER ====================
    const handleRefresh = async () => {
        if (isRefreshing || loading || isSearching) return;

        setIsRefreshing(true);
        try {
            if (isSearchMode) {
                // Refresh in search mode — re-run search
                await fetchSegments(0, pageSize, debouncedSearchTerm);
            } else {
                // Refresh current page
                await fetchSegments(currentPage - 1, pageSize, '');
            }
        } catch (err) {
            // fetchSegments handles its own error toasts
        } finally {
            setIsRefreshing(false);
        }
    };

    // ==================== SELECTION HANDLERS ====================
    const toggleSelect = (id: string) => {
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = (checked: boolean) => {
        if (checked) setSelected(filteredSegments.map((s) => s.id));
        else setSelected([]);
    };

    const allSelected =
        filteredSegments.length > 0 &&
        filteredSegments.every((s) => selected.includes(s.id));

    // ==================== ACTIONS ====================
    const handleEdit = (segment: Segment) => {
        const encodedName = segment.name.replace(/ /g, '-');
        navigate(`/audience/segments/edit/${encodedName}`, {
            state: { segmentData: segment },
        });
    };

    const handleDeleteClick = (segment: Segment) => {
        setSegmentToDelete(segment);
        setShowDeleteModal(true);
        setOpenMenuId(null);
    };

    const confirmDelete = async () => {
        if (!segmentToDelete) return;
        const { token, tenantId, userId } = getAuthContext();
        if (!token) return;

        setIsDeleting(true);
        try {
            const response = await fetch(
                `${BASE_URL}/v1/audience-insights/segments/${segmentToDelete.id}`,
                {
                    method: 'DELETE',
                    headers: {
                        'X-Tenant-Id': tenantId,
                        'X-User-Id': userId || '',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) throw new Error('Failed to delete segment');

            toast.success('Segment deleted successfully!');
            setShowDeleteModal(false);
            setSegmentToDelete(null);
            setSelected([]);

            // Refetch — using current search mode
            if (isSearchMode) {
                fetchSegments(0, pageSize, debouncedSearchTerm);
            } else {
                fetchSegments(currentPage - 1, pageSize, '');
            }
        } catch (error) {
            // console.error('❌ Delete failed:', error);
            toast.error('Failed to delete segment');
        } finally {
            setIsDeleting(false);
        }
    };

    const handlePageChange = (newPage: number) => {
        // newPage is 1-based
        if (newPage < 1 || newPage > totalPages || newPage === currentPage) return;
        setSelected([]);
        setCurrentPage(newPage);
        fetchSegments(newPage - 1, pageSize, '');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize);
        setCurrentPage(1);
        setSelected([]);
    };

    // ==================== RENDER ====================
    return (
        <div className="p-2">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
                <div>
                    <h2 className="text-[22px] font-bold text-[#3F5265]">Segments</h2>
                    <p className="text-[#657584] text-[15px]">Manage your Segments</p>
                </div>

                <button
                    onClick={() => navigate('/audience/segments/add/new-segment')}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-orange-500 text-white hover:bg-orange-600 text-sm font-medium"
                >
                    <FiPlusComponent size={16} /> Create segment
                </button>
            </div>

            {/* Card */}
            <div className="bg-white rounded-md border border-gray-200 shadow-sm">
                {/* Search + Refresh */}
                <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                    <div className="relative w-full max-w-xs">
                        <FiSearchComponent
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search Segments"
                            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-md text-sm text-gray-700 bg-gray-50 focus:outline-none focus:ring-1 focus:ring-orange-400"
                        />
                    </div>

                    {/* ⭐ Refresh button */}
                    <button
                        onClick={handleRefresh}
                        disabled={loading || isSearching || isRefreshing}
                        className={`p-2 text-gray-500 hover:text-teal-600 transition-colors ${loading || isSearching || isRefreshing ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        title="Refresh data"
                    >
                        <FiRefreshCwComponent
                            size={18}
                            className={loading || isSearching || isRefreshing ? 'animate-spin' : ''}
                        />
                    </button>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-[#657584] uppercase text-[11px] font-semibold">
                            <tr>
                                {/* <th className="w-10 px-4 py-3 text-left">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={(e) => toggleSelectAll(e.target.checked)}
                                        className="rounded border-gray-300"
                                    />
                                </th> */}
                                <th className="px-4 py-3 text-left">Name</th>
                                <th className="px-4 py-3 text-left">Create Date</th>
                                <th className="px-4 py-3 text-left">Last Modification</th>
                                <th className="px-4 py-3 text-left">Action</th>
                            </tr>
                        </thead>

                        <tbody>
                            {loading || isSearching ? (
                                <tr>
                                    <td colSpan={5} className="py-10 text-center text-gray-500">
                                        {isSearching ? 'Searching segments...' : 'Loading segments...'}
                                    </td>
                                </tr>
                            ) : filteredSegments.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="py-10 text-center text-gray-500">
                                        No segments found
                                    </td>
                                </tr>
                            ) : (
                                filteredSegments.map((segment) => (
                                    <tr
                                        key={segment.id}
                                        className="border-t border-gray-100 hover:bg-gray-50"
                                    >
                                        {/* <td className="px-4 py-4">
                                            <input
                                                type="checkbox"
                                                checked={selected.includes(segment.id)}
                                                onChange={() => toggleSelect(segment.id)}
                                                className="rounded border-gray-300"
                                            />
                                        </td> */}

                                        <td className="px-4 py-4 text-[#3F5265] font-medium">
                                            {segment.name}
                                        </td>

                                        <td className="px-4 py-4 text-[#657584]">
                                            {formatDate(segment.createdAt)}
                                        </td>

                                        <td className="px-4 py-4 text-[#657584]">
                                            {formatDate(segment.updatedAt)}
                                        </td>

                                        <td className="px-4 py-4">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleEdit(segment)}
                                                    className="inline-flex items-center gap-1 text-[#00899E] hover:underline text-sm font-medium"
                                                >
                                                    <FiEdit3Component size={14} /> Edit
                                                </button>

                                                {/* Kebab menu */}
                                                <div className="relative">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenMenuId(
                                                                openMenuId === segment.id ? null : segment.id
                                                            );
                                                        }}
                                                        className="p-1 rounded hover:bg-gray-100 text-gray-500"
                                                    >
                                                        <FiMoreVerticalComponent size={16} />
                                                    </button>

                                                    {openMenuId === segment.id && (
                                                        <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                                                            <button
                                                                onClick={() => handleDeleteClick(segment)}
                                                                className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                            >
                                                                <FiTrash2Component size={14} /> Delete
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer - hidden during search mode */}
                {!isSearchMode && (
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-5 py-4 border-t border-gray-100">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <span>Showing</span>
                            <select
                                value={pageSize}
                                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                className="border border-gray-200 rounded px-2 py-1 text-sm bg-gray-50"
                            >
                                {[5, 10, 20, 50].map((n) => (
                                    <option key={n} value={n}>
                                        {n}
                                    </option>
                                ))}
                            </select>
                            <span>results per page out of {totalElements}</span>
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                « FIRST
                            </button>

                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
                            >
                                <FiChevronLeftComponent size={12} /> PREV
                            </button>

                            {getPageNumbers(currentPage, totalPages).map((p, idx) =>
                                p === '...' ? (
                                    <span key={`e-${idx}`} className="px-2 text-gray-400">
                                        …
                                    </span>
                                ) : (
                                    <button
                                        key={p}
                                        onClick={() => handlePageChange(p)}
                                        className={`min-w-[32px] px-2 py-1.5 text-xs font-medium border rounded ${p === currentPage
                                            ? 'bg-white border-orange-500 text-orange-500'
                                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                    >
                                        {p}
                                    </button>
                                )
                            )}

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
                            >
                                NEXT <FiChevronRightComponent size={12} />
                            </button>

                            <button
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                LAST »
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Delete Confirm Modal */}
            {showDeleteModal && segmentToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-96 shadow-xl relative">
                        <button
                            onClick={() => {
                                setShowDeleteModal(false);
                                setSegmentToDelete(null);
                            }}
                            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                        >
                            <FiXComponent size={18} />
                        </button>

                        <h3 className="text-lg font-semibold text-red-600 mb-2">
                            Delete Segment
                        </h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete{' '}
                            <strong>"{segmentToDelete.name}"</strong>?
                            <br />
                            This action cannot be undone.
                        </p>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setSegmentToDelete(null);
                                }}
                                disabled={isDeleting}
                                className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                disabled={isDeleting}
                                className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60"
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={3000} />
        </div>
    );
};

export default Segments;